import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

/**
 * Fetches all agent sessions associated with comments on a given page.
 * Used by useCommentAgentSession to seed snapshot state on page load / page reload,
 * so the hook can immediately show the correct session state without waiting for a
 * new @-mention (chat-new pubsub) event.
 *
 * Source: Raman's graphql-central-schema PR #16497 — allCommentAgentSessions on Content type.
 *
 * Each node provides:
 *   - conversationId: feed into useCommentAgentSession
 *   - subentityId:    the comment ARI — maps back to a specific comment
 *   - agentSession.state: snapshot state (WORKING | ARTEFACT_READY | INPUT_REQUIRED | FAILED)
 *
 * Note: subentityId is exposed via a follow-up Sky Bridge PR. Until it lands, the
 * mapping from session → comment will need to come from another source.
 *
 * Paging: `first`/`after` are declared as variables rather than literals so the query text
 * (and therefore the persisted-query id) is the same whether or not `cc_ai_agentic_sessions` is
 * on. When the experiment is OFF both are passed as null, which is indistinguishable from
 * omitting them server-side, so the request is identical to the pre-paging one. `pageInfo` is
 * always selected — it is additive and unused when the experiment is off.
 *
 * `sort` is deliberately NOT requested. The ERS index that serves this query
 * (`by-entity-id-user-account-id`) has `deleted` as its range key, not a timestamp, so a sort
 * is not index-covered and would make Skybridge fall back to a full partition scan. See SES-9
 * (index investigation) — add a sort only once a covering index exists.
 */
export const CommentAgentSessionsQuery: GraphQLTaggedNode = graphql`
	query CommentAgentSessionsQuery($pageId: ID!, $status: [String!], $first: Int, $after: String) {
		content(id: $pageId, status: $status) {
			nodes {
				id
				allCommentAgentSessions(first: $first, after: $after) {
					edges {
						node {
							conversationId
							subEntityId
							agentSession {
								state
								agent {
									id
									name
									picture
								}
							}
						}
					}
					pageInfo {
						hasNextPage
						endCursor
					}
				}
			}
		}
	}
`;
